import { useEffect, useState } from 'react';

import { useDebounce } from '@mint-lib/hooks';
import { useTranslation } from '@mint-lib/i18n';
import { Skeleton, typographyVariants } from '@mint-lib/tw-components';
import { SearchBar } from '@mint-lib/tw-components/patterns';
import { cn } from '@mint-lib/tw-utils';

import {
  IntegrationStatusFilters,
  sortFilter,
} from '../../../constants/integrationsList.js';
import { useIntegrationsFilter } from '../../../hooks/useIntegrationsFilter.js';
import Filters from '../../_molecules/Filter/index.js';
import FilterSortBy from '../../_molecules/FilterSortBy/index.jsx';

const IntegrationsHeader = ({
  isIntegrationsLoading,
}: {
  isIntegrationsLoading: boolean;
}) => {
  const { t } = useTranslation('@myn/integrations_v2');

  const {
    navigate,
    search,
    sort,
    status,
    categoryUrl: category,
    channelUrl: channel,
  } = useIntegrationsFilter();

  const [searchValue, setSearchValue] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<IntegrationStatusFilters>(
    IntegrationStatusFilters.all,
  );
  const [sortField, setSortField] = useState<sortFilter>(
    sortFilter.connectionType,
  );

  const debouncedSearch = useDebounce(searchValue, 500);

  const chooseOption = (value: string) => {
    setSortField(value as sortFilter);
  };

  useEffect(() => {
    setSearchValue(search ?? '');
    setStatusFilter(status ?? IntegrationStatusFilters.all);
    setSortField(sort ?? sortFilter.connectionType);
  }, []);

  useEffect(() => {
    const searchState = {
      search: debouncedSearch,
      status: statusFilter,
      sort: sortField,
      category,
      channel,
    };
    navigate({
      search: searchState,
      state: {
        // @ts-expect-error We need to pass the state via URL params
        key: 'integrations',
        ...searchState,
      },
    });
  }, [debouncedSearch, statusFilter, sortField, navigate]);

  return (
    <div className="mb-6 flex h-auto w-full flex-col items-start">
      <h2 className={cn(typographyVariants({ variant: 'h2' }), 'mb-3')}>
        {t('Platform list')}
      </h2>
      <div className="flex w-full flex-row items-center justify-between">
        {isIntegrationsLoading ? (
          <>
            <Skeleton
              className="h-10 w-110"
              variant="rounded"
              data-testid="skeleton"
            />
            <div className="flex gap-2">
              <Skeleton
                className="h-10 w-67"
                variant="rounded"
                data-testid="skeleton"
              />
              <Skeleton
                className="h-10 w-21"
                variant="rounded"
                data-testid="skeleton"
              />
            </div>
          </>
        ) : (
          <>
            <Filters
              activeStatus={statusFilter}
              onFilterChange={(status) => {
                setStatusFilter(status);
              }}
            />

            <div className="mr-6 flex flex-row items-center justify-center gap-6">
              <SearchBar
                value={searchValue}
                color="default"
                onChange={({ target }) =>
                  setSearchValue((target as HTMLInputElement).value)
                }
                onClear={() => {
                  setSearchValue('');
                }}
                placeholder={t('What are you looking for today?')}
                size="medium"
                className="w-80"
              />
              <FilterSortBy selected={sortField} sortFnBy={chooseOption} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default IntegrationsHeader;
